/* eslint-disable */
// Programme row components used by EventDetail. Extracted in iter-124 from
// EventDetail.jsx (which was >4400 LOC) to make the page more navigable.
//
// Three exports:
//   * programmeTitleAndSubtitle(item)  — pure helper used by both rows
//   * <ReadOnlyProgrammeRow ... />     — non-admin view, no controls
//   * <SortableProgrammeRow ... />     — admin view with drag, up/down, edit,
//                                         replace, remove
//
// Both rows render an interval, a TBC placeholder, or a regular music piece
// using consistent colour-coded chrome.
import React from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Coffee, Clock, Pencil, ArrowRightLeft, ArrowUp, ArrowDown, Trash2,
  GripVertical, Bookmark,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";


export const programmeTitleAndSubtitle = (item) => {
  const isInterval = item.kind === "interval";
  const isPlaceholder = item.kind === "placeholder";
  let title;
  let subtitle;
  if (isInterval) {
    title = item.interval_label || item.music_title || "Interval";
    subtitle = item.interval_duration_min
      ? `Duration: ${item.interval_duration_min} min`
      : "Programme break";
  } else if (isPlaceholder) {
    title = item.placeholder_title || item.music_title || "TBC";
    subtitle = "Piece to be confirmed";
  } else {
    title = item.music_details?.title || item.music_title || "";
    subtitle = `${item.music_details?.composer || ""}${
      item.music_details?.arranger ? ` (arr. ${item.music_details.arranger})` : ""
    }`;
  }
  return { isInterval, isPlaceholder, title, subtitle };
};


export const ReadOnlyProgrammeRow = ({ item, index, displayNumber }) => {
  const { isInterval, isPlaceholder, title, subtitle } = programmeTitleAndSubtitle(item);
  const isSpare = !!item.is_spare;
  const rowStyle = isInterval
    ? "bg-amber-500/10 border border-amber-500/20"
    : isPlaceholder
      ? "bg-blue-500/5 border border-dashed border-blue-400/30"
      : isSpare
        ? "bg-secondary/15 border border-dashed border-white/10 opacity-75"
        : "bg-secondary/30";
  const numStyle = isInterval
    ? "bg-amber-500/20 text-amber-300"
    : isPlaceholder
      ? "bg-blue-500/15 text-blue-300"
      : isSpare
        ? "bg-muted text-muted-foreground"
        : "bg-primary/10 text-primary";
  return (
    <div
      className={`flex items-center justify-between p-4 rounded-lg ${rowStyle}`}
      data-testid={`programme-row-${item.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm flex-shrink-0 ${numStyle}`}
        >
          {isInterval ? <Coffee className="w-4 h-4" /> : isSpare ? "S" : (displayNumber ?? index + 1)}
        </div>
        <div className="min-w-0">
          <p className="font-medium truncate flex items-center gap-2">
            {title}
            {isPlaceholder && (
              <Badge
                variant="secondary"
                className="bg-blue-500/15 text-blue-300 border-blue-400/30 text-[10px] px-1.5 py-0"
              >
                TBC
              </Badge>
            )}
            {isSpare && !isInterval && (
              <Badge
                variant="secondary"
                className="bg-muted text-muted-foreground border-white/10 text-[10px] px-1.5 py-0"
                data-testid={`programme-spare-badge-${item.id}`}
              >
                Spare
              </Badge>
            )}
          </p>
          <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
        </div>
      </div>
      {!isInterval && !isPlaceholder && item.music_details?.duration_minutes && (
        <Badge variant="outline" className="text-xs flex-shrink-0">
          <Clock className="w-3 h-3 mr-1" />
          {item.music_details.duration_minutes} min
        </Badge>
      )}
    </div>
  );
};


export const SortableProgrammeRow = ({
  item, index, total, reorderBusy, displayNumber,
  onMoveUp, onMoveDown, onRemove, onEditPlaceholder, onReplacePlaceholder,
  onToggleSpare,
}) => {
  const { isInterval, isPlaceholder, title, subtitle } = programmeTitleAndSubtitle(item);
  const isSpare = !!item.is_spare;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  const rowStyle = isInterval
    ? "bg-amber-500/10 border border-amber-500/20"
    : isPlaceholder
      ? "bg-blue-500/5 border border-dashed border-blue-400/30 hover:bg-blue-500/10"
      : isSpare
        ? "bg-secondary/15 border border-dashed border-white/10 opacity-80 hover:opacity-100"
        : "bg-secondary/30 hover:bg-secondary/50";
  const numStyle = isInterval
    ? "bg-amber-500/20 text-amber-300"
    : isPlaceholder
      ? "bg-blue-500/15 text-blue-300"
      : isSpare
        ? "bg-muted text-muted-foreground"
        : "bg-primary/10 text-primary";
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${rowStyle} ${isDragging ? "ring-2 ring-primary/60 shadow-lg" : ""}`}
      data-testid={`programme-row-${item.id}`}
      {...attributes}
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* Drag handle — long-press on mobile, click-drag on desktop */}
        <button
          type="button"
          {...listeners}
          className="touch-none p-1 -m-1 text-muted-foreground/60 hover:text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0"
          aria-label="Drag to reorder"
          data-testid={`programme-drag-handle-${item.id}`}
        >
          <GripVertical className="w-5 h-5" />
        </button>
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm flex-shrink-0 ${numStyle}`}
        >
          {isInterval ? <Coffee className="w-4 h-4" /> : isSpare ? "S" : (displayNumber ?? index + 1)}
        </div>
        <div className="min-w-0">
          <p className="font-medium truncate flex items-center gap-2">
            {title}
            {isPlaceholder && (
              <Badge
                variant="secondary"
                className="bg-blue-500/15 text-blue-300 border-blue-400/30 text-[10px] px-1.5 py-0"
              >
                TBC
              </Badge>
            )}
            {isSpare && !isInterval && (
              <Badge
                variant="secondary"
                className="bg-muted text-muted-foreground border-white/10 text-[10px] px-1.5 py-0"
                data-testid={`programme-spare-badge-${item.id}`}
              >
                Spare
              </Badge>
            )}
          </p>
          <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {!isInterval && !isPlaceholder && item.music_details?.duration_minutes && (
          <Badge variant="outline" className="text-xs mr-1">
            <Clock className="w-3 h-3 mr-1" />
            {item.music_details.duration_minutes} min
          </Badge>
        )}
        {!isInterval && onToggleSpare && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${isSpare ? "text-amber-400 hover:text-amber-300" : "text-muted-foreground/60 hover:text-muted-foreground"}`}
            onClick={onToggleSpare}
            data-testid={`programme-spare-toggle-${item.id}`}
            aria-label={isSpare ? "Move back into the set list" : "Mark as spare"}
            title={isSpare ? "Move back into the set list" : "Mark as spare (held in reserve)"}
          >
            <Bookmark className={`w-4 h-4 ${isSpare ? "fill-current" : ""}`} />
          </Button>
        )}
        {isPlaceholder && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-blue-300 hover:text-blue-200"
              onClick={onEditPlaceholder}
              data-testid={`programme-edit-placeholder-${item.id}`}
              aria-label="Edit TBC title"
              title="Edit title"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-blue-300 hover:text-blue-200"
              onClick={onReplacePlaceholder}
              data-testid={`programme-replace-placeholder-${item.id}`}
              aria-label="Replace with a real piece"
              title="Replace with a piece from the library"
            >
              <ArrowRightLeft className="w-4 h-4" />
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 disabled:opacity-30"
          disabled={index === 0 || reorderBusy}
          onClick={onMoveUp}
          data-testid={`programme-up-${item.id}`}
          aria-label="Move up"
        >
          <ArrowUp className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 disabled:opacity-30"
          disabled={index === total - 1 || reorderBusy}
          onClick={onMoveDown}
          data-testid={`programme-down-${item.id}`}
          aria-label="Move down"
        >
          <ArrowDown className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={onRemove}
          data-testid={`programme-remove-${item.id}`}
          aria-label="Remove"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
